import React, { ElementType } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { FieldError } from "react-hook-form";

type SEFilterSelectError = {
  message?: string;
};

type SEFilterSelectProps = {
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: any;
  defaultOption?: any;
  defaultOptionValue?: any;
  options: any[];
  onChange: (value: any) => void;
  customLabel?: (value: any) => void;
  displayField?: string;
  valueField?: string;
  IconComponent?: ElementType<any> | undefined;
  error?: SEFilterSelectError | FieldError | Error | undefined;
};

export const SEFilterSelect: React.FC<SEFilterSelectProps> = ({
  id,
  label,
  value,
  onChange,
  customLabel,
  placeholder,
  disabled,
  options = [],
  valueField,
  displayField,
  defaultOption,
  defaultOptionValue = "",
  error,
  ...props
}) => {
  const renderOptions = React.useMemo(() => {
    return options?.map((option, index) => {
      let optionValue = valueField ? option[valueField] : option;
      const optionLabel = displayField
        ? option?.hasOwnProperty(displayField) && option[displayField]
        : option;
      const isObject = optionValue !== null && typeof optionValue === "object";
      if (isObject && !valueField) {
        optionValue = JSON.stringify(optionValue);
      }

      return (
        <option key={index} value={optionValue}>
          {!!customLabel ? customLabel(optionLabel) : optionLabel}
        </option>
      );
    });
  }, [options, valueField, displayField, customLabel]);

  function isJSONString(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  function parseObjectToString(value: any) {
    if (value === defaultOptionValue) return defaultOptionValue;
    if (value === undefined) return "";
    const isObject = value !== null && typeof value === "object";
    return isObject ? JSON.stringify(value) : value;
  }

  const handleOnChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    const parsedValue = isJSONString(value) ? JSON.parse(value) : value;
    onChange(parsedValue);
  };

  return (
    <FormControl fullWidth>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        disabled={disabled}
        label={label}
        placeholder={placeholder}
        value={parseObjectToString(value)}
        onChange={handleOnChange}
        native
      >
        {!!defaultOption && (
          <option value={defaultOptionValue}>{defaultOption}</option>
        )}
        {renderOptions}
      </Select>
      {error && (
        <p style={{ color: "red", marginTop: "8px" }}>{error.message}</p>
      )}
    </FormControl>
  );
};

export default SEFilterSelect;
